/* IntegrationHealthPanel.jsx — External Infrastructure Connectivity surface.
 *
 * Composes Integration Health + a Unified Activity Stream snippet for
 * Mission Control. Includes connect/disconnect/retry inline controls.
 * Full surface lives at /integrations.
 */
import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Plug, Mail, Calendar, PenLine, Search, BadgeCheck, Building2,
  ShieldCheck, Activity, ArrowUpRight, RotateCw, Power, Zap,
} from "lucide-react";
import { api } from "../../lib/api";
import { MissionPanelSkeleton } from "../ui/operational-skeleton";

const ICON_MAP = {
  Building2, Mail, Calendar, PenLine, Search, BadgeCheck, ShieldCheck,
};

const STATUS_TONE = {
  healthy:      { fg: "#047857", bg: "rgba(16,185,129,0.14)", dot: "#10B981", label: "Connected" },
  watching:     { fg: "#92400E", bg: "rgba(245,158,11,0.12)", dot: "#F59E0B", label: "Slow sync" },
  degraded:     { fg: "#9F1239", bg: "rgba(244,63,94,0.10)",  dot: "#F43F5E", label: "Attention" },
  disconnected: { fg: "#475569", bg: "rgba(148,163,184,0.10)", dot: "#94A3B8", label: "Not connected" },
};

const PIcon = ({ name, size = 12 }) => {
  const Cmp = ICON_MAP[name] || Plug;
  return <Cmp size={size} strokeWidth={2.3} />;
};

/* ─── Hero ─────────────────────────────────────────────────────── */
const InfraHero = ({ summary, narrative }) => (
  <div
    className="relative overflow-hidden rounded-2xl px-5 sm:px-6 py-5 mb-5"
    data-testid="infra-hero"
    style={{
      background: "linear-gradient(135deg, #050818 0%, #15183E 55%, #2A2570 100%)",
      boxShadow: "0 24px 60px -32px rgba(15,23,42,0.55), inset 0 0 0 1px rgba(255,255,255,0.05)",
    }}
  >
    <span aria-hidden className="absolute -top-10 -left-8 w-[260px] h-[260px] rounded-full blur-3xl pointer-events-none"
      style={{ background: "radial-gradient(circle, rgba(56,189,248,0.30), transparent 65%)" }} />
    <div className="relative">
      <div className="inline-flex items-center gap-2 mb-2">
        <Zap size={12} className="text-sky-300" strokeWidth={2.3} />
        <p className="text-[9.5px] uppercase tracking-[0.22em] font-bold text-sky-200/90">
          Integration Infrastructure
        </p>
      </div>
      <p className="text-[13px] text-white/85 leading-relaxed max-w-2xl mb-4"
         data-testid="infra-narrative">
        {narrative}
      </p>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
        <Stat label="Connected"  value={`${summary?.providers_connected ?? 0}/${summary?.providers_total ?? 0}`} accent="#38BDF8" testid="infra-stat-connected" />
        <Stat label="Healthy"    value={summary?.providers_healthy ?? 0}    accent="#10B981" testid="infra-stat-healthy" />
        <Stat label="Attention"  value={summary?.providers_degraded ?? 0}   accent="#F43F5E" testid="infra-stat-degraded" />
        <Stat label="Avg sync"   value={summary?.avg_hours_since_sync != null ? `${summary.avg_hours_since_sync}h` : "—"} accent="#A78BFA" testid="infra-stat-sync" />
      </div>
    </div>
  </div>
);

const Stat = ({ label, value, accent, testid }) => (
  <div
    className="rounded-lg px-3 py-2.5"
    style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.08)" }}
    data-testid={testid}
  >
    <p className="text-[9px] uppercase tracking-[0.16em] font-bold text-white/55">{label}</p>
    <p className="text-[16px] font-semibold tabular-nums" style={{ color: accent }}>{value}</p>
  </div>
);

/* ─── Section Header ───────────────────────────────────────────── */
const SubHeader = ({ icon: Icon, eyebrow, title, action }) => (
  <div className="flex items-center gap-2 mb-2.5">
    <div className="w-6 h-6 rounded-md inline-flex items-center justify-center"
      style={{ background: "rgba(99,102,241,0.10)", color: "#4338CA" }}>
      <Icon size={11} strokeWidth={2.3} />
    </div>
    <div className="min-w-0 flex-1">
      <p className="text-[9.5px] uppercase tracking-[0.16em] font-bold text-violet-700">{eyebrow}</p>
      <h3 className="text-[12.5px] font-semibold text-slate-900 truncate">{title}</h3>
    </div>
    {action}
  </div>
);

/* ─── Provider row ─────────────────────────────────────────────── */
const ProviderRow = ({ p, onConnect, onDisconnect, onRetry, busyKey }) => {
  const t = STATUS_TONE[p.status] || STATUS_TONE.disconnected;
  const busy = busyKey === p.key;
  return (
    <div className="flex items-center gap-3 px-3 py-2.5 rounded-md hover:bg-slate-50/70 transition-colors"
         data-testid={`infra-provider-${p.key}`}>
      <div className="w-9 h-9 rounded-lg inline-flex items-center justify-center shrink-0"
        style={{ background: t.bg, color: t.fg }}>
        <PIcon name={p.icon} size={14} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <p className="text-[12.5px] font-semibold text-slate-900 truncate">{p.label}</p>
          <span aria-hidden className="w-1 h-1 rounded-full bg-slate-300" />
          <p className="text-[10.5px] uppercase tracking-[0.12em] font-bold" style={{ color: t.fg }}>
            {p.status_label}
          </p>
        </div>
        <p className="text-[11px] text-slate-500 truncate">{p.commentary}</p>
      </div>
      {p.status === "disconnected" ? (
        <button
          type="button"
          onClick={() => onConnect?.(p.key)}
          disabled={busy}
          className="h-7 px-2.5 rounded-md text-[10.5px] font-semibold shrink-0"
          style={{
            background: busy ? "rgba(99,102,241,0.15)" : "linear-gradient(135deg, #4F46E5 0%, #7C3AED 100%)",
            color: "#FFFFFF", opacity: busy ? 0.7 : 1,
          }}
          data-testid={`infra-connect-${p.key}`}
        >
          Connect
        </button>
      ) : (
        <div className="flex items-center gap-1 shrink-0">
          <button
            type="button"
            onClick={() => onRetry?.(p.key)}
            disabled={busy}
            className="h-7 px-2 rounded-md text-[10.5px] font-semibold inline-flex items-center gap-1"
            style={{ background: "rgba(99,102,241,0.10)", color: "#4338CA" }}
            data-testid={`infra-retry-${p.key}`}
            title="Force sync"
          >
            <RotateCw size={9} strokeWidth={2.4} className={busy ? "animate-spin" : ""} />
            Sync
          </button>
          <button
            type="button"
            onClick={() => onDisconnect?.(p.key)}
            disabled={busy}
            className="h-7 px-2 rounded-md text-[10.5px] font-semibold inline-flex items-center gap-1"
            style={{ background: "rgba(244,63,94,0.08)", color: "#9F1239" }}
            data-testid={`infra-disconnect-${p.key}`}
            title="Disconnect"
          >
            <Power size={9} strokeWidth={2.4} />
          </button>
        </div>
      )}
    </div>
  );
};

/* ─── Activity stream row ──────────────────────────────────────── */
const SOURCE_DOT = {
  email:     "#38BDF8",
  calendar:  "#A78BFA",
  signing:   "#10B981",
  document:  "#F59E0B",
  searches:  "#0EA5E9",
  compliance:"#9F1239",
  milestone: "#7C3AED",
  operational:"#94A3B8",
};

const ActivityRow = ({ r }) => {
  const t = new Date(r.happened_at);
  const when = isNaN(t) ? "" : t.toLocaleString();
  return (
    <div className="flex items-center gap-3 px-3 py-2" data-testid={`infra-stream-${r.id}`}>
      <span aria-hidden className="w-1.5 h-1.5 rounded-full shrink-0"
        style={{ background: SOURCE_DOT[r.source] || "#94A3B8" }} />
      <p className="text-[11px] uppercase tracking-[0.12em] font-bold w-20 text-slate-500 shrink-0">
        {r.source}
      </p>
      <p className="text-[12px] font-medium text-slate-800 flex-1 truncate">
        {r.label}
        {r.deal_name ? <span className="text-slate-400"> · {r.deal_name}</span> : null}
      </p>
      <p className="text-[10.5px] tabular-nums text-slate-500 shrink-0">{when}</p>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════════
   Main Panel
   ─────────────────────────────────────────────────────────────────── */
export const IntegrationHealthPanel = () => {
  const nav = useNavigate();
  const [data, setData]     = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]   = useState(null);
  const [busy, setBusy]     = useState(null);

  const load = useCallback(async () => {
    try {
      const { data: resp } = await api.get("/api/integrations/overview");
      setData(resp?.integration || null);
    } catch (err) {
      if (err?.response?.status === 403) setError("Leadership only.");
      else setError("Could not load Integration Infrastructure.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const onConnect = async (key) => {
    setBusy(key);
    try {
      await api.post(`/api/integrations/providers/${key}/connect`);
      toast.success("Connected.");
      await load();
    } catch (e) {
      toast.error("Could not connect.");
    } finally { setBusy(null); }
  };
  const onDisconnect = async (key) => {
    setBusy(key);
    try {
      await api.post(`/api/integrations/providers/${key}/disconnect`);
      toast.success("Disconnected.");
      await load();
    } catch (e) {
      toast.error("Could not disconnect.");
    } finally { setBusy(null); }
  };
  const onRetry = async (key) => {
    setBusy(key);
    try {
      await api.post(`/api/integrations/providers/${key}/retry`);
      toast.success("Sync triggered.");
      await load();
    } catch (e) {
      toast.error("Could not trigger sync.");
    } finally { setBusy(null); }
  };

  if (error) return null;
  if (loading) return <MissionPanelSkeleton tone="light" testid="integration-section-loading" />;
  if (!data) return null;

  const health = data.integration_health || {};
  const stream = data.activity_stream || [];

  return (
    <section className="cf-hydrate-fade mb-7" data-testid="mission-control-integration-health">
      <div className="flex items-center gap-2.5 mb-3.5">
        <div className="w-7 h-7 rounded-md inline-flex items-center justify-center"
          style={{ background: "rgba(56,189,248,0.10)", color: "#0369A1" }}>
          <Plug size={13} strokeWidth={2.3} />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-[10px] uppercase tracking-[0.16em] font-bold text-sky-700">
            Integration Infrastructure
          </p>
          <h2 className="text-[13.5px] font-semibold text-slate-900 truncate">
            External connectivity, unified activity stream, sync health
          </h2>
        </div>
        <button
          type="button"
          onClick={() => nav("/integrations")}
          className="inline-flex items-center gap-1 h-7 px-2.5 rounded-md text-[10.5px] font-semibold"
          style={{
            background: "rgba(56,189,248,0.10)", color: "#0369A1",
            border: "1px solid rgba(56,189,248,0.22)",
          }}
          data-testid="infra-open-full"
        >
          Full view
          <ArrowUpRight size={10} strokeWidth={2.4} />
        </button>
      </div>

      <InfraHero summary={data.summary} narrative={data.narrative} />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <div className="gp-card p-4" data-testid="infra-providers-list">
          <SubHeader icon={Plug} eyebrow="Provider Health"
            title={`${health.counts?.healthy || 0} healthy · ${health.counts?.degraded || 0} attention`} />
          <div className="space-y-1 max-h-[420px] overflow-auto pr-1">
            {(health.providers || []).map((p) => (
              <ProviderRow key={p.key} p={p} busyKey={busy}
                onConnect={onConnect} onDisconnect={onDisconnect} onRetry={onRetry} />
            ))}
          </div>
        </div>
        <div className="gp-card p-4" data-testid="infra-activity-stream">
          <SubHeader icon={Activity} eyebrow="Unified Activity Stream"
            title="Blended cross-system operational events" />
          <div className="space-y-0 max-h-[420px] overflow-auto pr-1 divide-y divide-slate-100">
            {stream.slice(0, 14).map((r) => (
              <ActivityRow key={r.id} r={r} />
            ))}
            {stream.length === 0 ? (
              <p className="text-[11.5px] text-slate-500 italic px-2">
                The activity stream will populate as integrations sync.
              </p>
            ) : null}
          </div>
        </div>
      </div>
    </section>
  );
};

export default IntegrationHealthPanel;
